import { Button, Vec3, _decorator, Color, UIRenderer, math } from 'cc';
import { EDITOR } from 'cc/env';
import HSLAdjuster from '../Shader/HSLAdjuster';

const { ccclass, property } = _decorator;

@ccclass('CustomButtonStatus')
export class CustomButtonStatus {
    @property(Vec3)
    hsl = Vec3.ZERO.clone();
    @property
    enableScale = true;
    @property({
        visible(this: CustomButtonStatus) {
            return this.enableScale;
        },
    })
    scale = Vec3.ONE.clone();
    @property
    enableColor = true;
    @property({
        type: Color,
        visible(this: CustomButtonStatus) {
            return this.enableColor;
        },
    })
    color = Color.WHITE.clone();
    @property
    angle = 0;

    constructor();
    constructor(hsl: Vec3);
    constructor(hsl: Vec3, scale: Vec3);
    constructor(hsl: Vec3, scale: Vec3, color: Color);
    constructor(hsl: Vec3, scale: Vec3, color: Color, angle: number);
    constructor(hsl?: Vec3, scale?: Vec3, color?: Color, angle?: number) {
        this.hsl = hsl ?? this.hsl;
        this.scale = scale ?? this.scale;
        this.color = color ?? this.color;
        this.angle = angle ?? this.angle;
    }

    public static lerp(out: CustomButtonStatus, from: CustomButtonStatus, to: CustomButtonStatus, ratio: number): void {
        Vec3.lerp(out.hsl, from.hsl, to.hsl, ratio);
        Vec3.lerp(out.scale, from.scale, to.enableScale ? to.scale : from.scale, ratio);
        Color.lerp(out.color, from.color, to.enableColor ? to.color : from.color, ratio);
        out.angle = math.lerp(from.angle, to.angle, ratio);
    }

    public equal(status: CustomButtonStatus) {
        return this.hsl.equals(status.hsl) && this.scale.equals(status.scale) && this.color.equals(status.color) && this.angle === status.angle;
    }
}

const tempStatus = new CustomButtonStatus();

@ccclass('CustomButton')
export default class CustomButton extends Button {
    @property
    public readonly enableAngleTransition = false;

    @property(CustomButtonStatus)
    private _normalStatus: CustomButtonStatus = new CustomButtonStatus();

    @property(CustomButtonStatus)
    private _pressedStatus: CustomButtonStatus = new CustomButtonStatus();

    @property(CustomButtonStatus)
    private _hoverStatus: CustomButtonStatus = new CustomButtonStatus();

    @property(CustomButtonStatus)
    private _disabledStatus: CustomButtonStatus = new CustomButtonStatus();

    private _hslAdjuster: HSLAdjuster = null;
    private _childrenHSLAdjusters: HSLAdjuster[] = [];
    private _uiRenderer: UIRenderer = null;

    private _transitionFinished = true;
    private _time = 0;
    private _pressed = false;
    private _hovered = false;
    private _fromStatus: CustomButtonStatus = new CustomButtonStatus();
    private _toStatus: CustomButtonStatus = new CustomButtonStatus();

    @property
    get transitionDuration() {
        return this._duration;
    }
    set transitionDuration(value) {
        if (this._duration === value) {
            return;
        }
        this._duration = value;
    }

    @property(CustomButtonStatus)
    public get normalStatus() {
        return this._normalStatus;
    }
    public set normalStatus(value: CustomButtonStatus) {
        if (this._normalStatus?.equal(value)) {
            return;
        }
        this._normalStatus = value;
        this._updateState();
    }

    @property(CustomButtonStatus)
    public get pressedStatus(): CustomButtonStatus {
        return this._pressedStatus;
    }
    public set pressedStatus(value: CustomButtonStatus) {
        if (this._pressedStatus.equal(value)) {
            return;
        }
        this._pressedStatus = value;
    }

    @property(CustomButtonStatus)
    public get hoverStatus(): CustomButtonStatus {
        return this._hoverStatus;
    }
    public set hoverStatus(value: CustomButtonStatus) {
        if (this._hoverStatus.equal(value)) {
            return;
        }
        this._hoverStatus = value;
    }

    @property(CustomButtonStatus)
    public get disabledStatus(): CustomButtonStatus {
        return this._disabledStatus;
    }
    public set disabledStatus(value: CustomButtonStatus) {
        if (this._disabledStatus.equal(value)) {
            return;
        }
        this._disabledStatus = value;
        this._updateState();
    }

    public get interactable() {
        return super.interactable;
    }
    public set interactable(value) {
        super.interactable = value;
    }

    public update(dt: number) {
        if (this._transitionFinished || !this.target || this._fromStatus.equal(this._toStatus)) {
            return;
        }

        this._time += dt;
        let ratio = 1.0;
        if (this._duration > 0) {
            ratio = this._time / this._duration;
        }

        if (ratio >= 1) {
            ratio = 1;
        }

        CustomButtonStatus.lerp(tempStatus, this._fromStatus, this._toStatus, ratio);
        this._applyButtonStatus(tempStatus);

        if (ratio === 1) {
            this._transitionFinished = true;
        }
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _applyTarget() {
        if (this.target) {
            super._applyTarget();
            this._hslAdjuster = this.target.getComponent(HSLAdjuster);
            this._childrenHSLAdjusters = this.target.getComponentsInChildren(HSLAdjuster);
            this._uiRenderer = this.target.getComponent(UIRenderer);
        }
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _resetState() {
        super._resetState();
        this._applyButtonStatus(this._interactable ? this._normalStatus : this._disabledStatus);
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _applyTransition(state: string) {
        if (this.ignoreStates?.includes(state as Button.State)) {
            return;
        }
        if (this.keep && this.keepState) {
            state = this.keepState;
        }
        this.updateCustomTransition(state);
        if (this._transition === 2) {
            super._applyTransition(state);
        } else {
            this.node.emit(Button.EventType.APPLY_TRANSITION, state);
        }
    }

    protected updateCustomTransition(state: string) {
        if (!this.target) {
            return;
        }

        if (EDITOR) {
            this._applyButtonStatus(this[`${state}Status`]);
        } else {
            this._fromStatus = new CustomButtonStatus(this._hslAdjuster?.hsl, this.target.getScale(), this._uiRenderer?.color.clone(), this.target.angle);
            this._toStatus = this[`${state}Status`];
            this._time = 0;
            this._transitionFinished = false;
        }
    }

    private _applyButtonStatus(status: CustomButtonStatus) {
        if (!this.target) {
            return;
        }

        if (this.ignoreStates?.includes(this.getButtonState())) {
            return;
        }
        if (this.keep && this.keepState) {
            status = this[`${this.keepState}Status`];
        }

        // HSL
        if (this._hslAdjuster) {
            this._hslAdjuster.hsl = status.hsl;
            this._childrenHSLAdjusters.forEach((hslAdjuster) => {
                hslAdjuster.hsl = status.hsl;
            });
        }

        // color
        if (this._uiRenderer && status.enableColor) {
            this._uiRenderer.color = status.color;
        }

        // angle
        if (this.enableAngleTransition) {
            this.target.angle = status.angle;
        }

        // scale
        if (status.enableScale) {
            this.target.setScale(status.scale);
        }
    }
}
